import type { ReactElement } from 'react';
import EmberFlame from '../../../../../shared-web/EmberFlame';
import { Link } from './Link';
import { letterDateLine, ordinalLine, readsLine } from '../letter-format';
import './LetterCover.css';

export interface LetterCoverProps {
  /** Where the cover opens: the letter's page. */
  to: string;
  writtenAt: string | null | undefined;
  /** The first line of the letter, as prose; the cover clips it to two lines. */
  firstLine: string;
  /** Which letter and what days it reads, when the surface knows (the notebook does; the thread does not). */
  ordinal?: number | null;
  readsFrom?: string | null;
  /**
   * The picture of their path drawn with the letter (2026-11-04 founder: letter-cover.js), when its page has one:
   * the card is the picture, and under it what it opens on.
   */
  cover?: string | null;
  /** Opened in the notebook already. Unread covers carry the dot. */
  read?: boolean;
  /** `data-log` name of the tap; the letter id rides along. */
  log: string;
  letterId?: string | null;
  className?: string;
}

/**
 * The cover of one of Ember's weekly letters (§4.20, 2026-09-23 二次拍板):
 * the same object wherever a letter is announced — a card on the notebook's
 * Letters tab, a turn of the conversation — and opening it is opening the
 * letter's page, the way an Ember card opens the Ember. The letterhead band
 * (the flame, "A letter from Ember", the day), one or two facts when the
 * surface knows them, the first line in two lines, one door. The words wait
 * on the page; the cover only says that a letter is here and what it opens on.
 * A letter drawn with its path (2026-11-04 founder) shows that picture instead: their path, the Embers on it, how
 * many others stood there — and under it the day and the door to their path and the letter.
 */
export function LetterCover({ to, writtenAt, firstLine, ordinal = null, readsFrom = null, cover = null, read = true, log, letterId = null, className = '' }: LetterCoverProps): ReactElement {
  const reads = readsLine(readsFrom, writtenAt);
  const facts = ordinal ? `${ordinalLine(ordinal)}${reads ? ` · reads ${reads}` : ''}` : '';
  if (cover) {
    return (
      <Link
        to={to}
        className={`letter-cover has-picture${read ? ' is-read' : ' is-unread'}${className ? ` ${className}` : ''}`}
        data-log={log}
        data-log-letter-id={letterId ?? undefined}
        data-log-state={read ? 'read' : 'unread'}
        aria-label={`Your path and Ember's letter, ${letterDateLine(writtenAt)}${read ? '' : ', unread'}`}
      >
        <img className="letter-cover-picture" src={cover} alt="Your path, drawn" width={1200} height={630} loading="lazy" />
        <span className="letter-cover-foot">
          <span className="letter-cover-foot-words">
            <span className="letter-cover-when">
              {!read && <span className="letter-cover-dot" aria-hidden />}
              {read ? '' : 'New · '}{reads || letterDateLine(writtenAt, 'short')}
            </span>
            <span className="letter-cover-what">Your path, and Ember's letter</span>
          </span>
          <span className="letter-cover-door">Open</span>
        </span>
      </Link>
    );
  }
  return (
    <Link
      to={to}
      className={`letter-cover${read ? ' is-read' : ' is-unread'}${className ? ` ${className}` : ''}`}
      data-log={log}
      data-log-letter-id={letterId ?? undefined}
      data-log-state={read ? 'read' : 'unread'}
      aria-label={`A letter from Ember, ${letterDateLine(writtenAt)}${read ? '' : ', unread'}`}
    >
      <span className="letter-cover-band">
        <span className="letter-cover-from">
          <span className="letter-cover-mark" aria-hidden><EmberFlame className="letter-cover-flame" /></span>
          A letter from Ember
        </span>
        <span className="letter-cover-date">{letterDateLine(writtenAt, 'short')}</span>
        {!read && <span className="letter-cover-dot" aria-hidden />}
      </span>
      <span className="letter-cover-body">
        {facts && <span className="letter-cover-facts">{facts}</span>}
        <span className="letter-cover-line" dir="auto">{firstLine}</span>
      </span>
      <span className="letter-cover-open">
        <span>{read ? 'Read again' : 'Open the letter'}</span>
        <svg viewBox="0 0 24 24" aria-hidden><path d="M9 6l6 6-6 6" /></svg>
      </span>
    </Link>
  );
}
